import type {
  AdmissionsUpdate,
  Programme,
  Scholarship,
  StudentApplication,
  UniversityProfile,
} from '../types/admissions'

export const universityProfiles: UniversityProfile[] = [
  {
    id: 'inst-unilag',
    name: 'University of Lagos',
    shortName: 'UNILAG',
    country: 'Nigeria',
    state: 'Lagos',
    city: 'Lagos',
    type: 'Federal',
    founded: 1962,
    students: 42180,
    internationalIntake: true,
    campuses: ['Akoka Main Campus', 'Idi-Araba College of Medicine'],
    accommodationOnCampus: true,
    scholarshipsAvailable: 6,
    representative: 'Admissions Office (verified representative)',
    provenance: 'verified',
  },
  {
    id: 'inst-oau',
    name: 'Obafemi Awolowo University',
    shortName: 'OAU',
    country: 'Nigeria',
    state: 'Osun',
    city: 'Ile-Ife',
    type: 'Federal',
    founded: 1961,
    students: 35400,
    internationalIntake: false,
    campuses: ['Ile-Ife Main Campus'],
    accommodationOnCampus: true,
    scholarshipsAvailable: 3,
    provenance: 'user',
  },
  {
    id: 'inst-abu',
    name: 'Ahmadu Bello University',
    shortName: 'ABU',
    country: 'Nigeria',
    state: 'Kaduna',
    city: 'Zaria',
    type: 'Federal',
    founded: 1962,
    students: 48900,
    internationalIntake: true,
    campuses: ['Samaru Campus'],
    accommodationOnCampus: true,
    scholarshipsAvailable: 4,
    representative: 'Registry — ICT Directorate (verified representative)',
    provenance: 'verified',
  },
  {
    id: 'inst-cu',
    name: 'Covenant University',
    shortName: 'CU',
    country: 'Nigeria',
    state: 'Ogun',
    city: 'Ota',
    type: 'Private',
    founded: 2002,
    students: 9800,
    internationalIntake: true,
    campuses: ['Canaanland Campus'],
    accommodationOnCampus: true,
    scholarshipsAvailable: 2,
    representative: 'Office of the Registrar (verified representative)',
    provenance: 'verified',
  },
  {
    id: 'inst-tud',
    name: 'TU Delft',
    shortName: 'TU Delft',
    country: 'Netherlands',
    state: 'South Holland',
    city: 'Delft',
    type: 'International',
    founded: 1842,
    students: 27000,
    internationalIntake: true,
    campuses: ['Delft Campus'],
    accommodationOnCampus: false,
    scholarshipsAvailable: 5,
    representative: 'International Office (verified representative)',
    provenance: 'verified',
  },
  {
    id: 'inst-kth',
    name: 'KTH Royal Institute of Technology',
    shortName: 'KTH',
    country: 'Sweden',
    state: 'Stockholm',
    city: 'Stockholm',
    type: 'International',
    founded: 1827,
    students: 18500,
    internationalIntake: true,
    campuses: ['Valhallavägen Campus'],
    accommodationOnCampus: false,
    scholarshipsAvailable: 4,
    provenance: 'user',
  },
]

export const programmes: Programme[] = [
  {
    id: 'prg-1',
    universityId: 'inst-unilag',
    title: 'B.Sc. Systems Engineering',
    level: "Bachelor's",
    field: 'Engineering',
    durationMonths: 60,
    tuitionPerYear: 190000,
    currency: 'NGN',
    deliveryMode: 'On campus',
    language: 'English',
    deadline: '2026-09-30',
    deadlineLabel: 'Closes 30 Sep 2026',
    requirements: [
      'Five O-level credits including Mathematics, Physics and English',
      'UTME score of 240 or above',
      'Post-UTME screening',
    ],
    scholarshipIds: ['sch-1', 'sch-3'],
    careerOutcomes: ['Operations analyst', 'Systems engineer', 'Data analyst'],
    provenance: 'verified',
  },
  {
    id: 'prg-2',
    universityId: 'inst-unilag',
    title: 'M.Sc. Engineering Management',
    level: "Master's",
    field: 'Engineering',
    durationMonths: 18,
    tuitionPerYear: 620000,
    currency: 'NGN',
    deliveryMode: 'Blended',
    language: 'English',
    deadline: '2026-08-31',
    deadlineLabel: 'Closes 31 Aug 2026',
    requirements: [
      'Second class lower or better in an engineering discipline',
      'NYSC discharge certificate',
      'Two academic references',
    ],
    scholarshipIds: ['sch-3'],
    careerOutcomes: ['Project manager', 'Operations manager'],
    provenance: 'verified',
  },
  {
    id: 'prg-3',
    universityId: 'inst-tud',
    title: 'M.Sc. Sustainable Energy Technology',
    level: "Master's",
    field: 'Energy systems',
    durationMonths: 24,
    tuitionPerYear: 22600,
    currency: 'EUR',
    deliveryMode: 'On campus',
    language: 'English',
    deadline: '2027-01-15',
    deadlineLabel: 'Closes 15 Jan 2027',
    requirements: [
      "Bachelor's in engineering or physical sciences",
      'IELTS 6.5 overall, no band below 6.0',
      'Motivation letter and CV',
      'Two academic references',
    ],
    scholarshipIds: ['sch-2', 'sch-4'],
    careerOutcomes: ['Energy systems engineer', 'Grid analyst', 'Research engineer'],
    provenance: 'verified',
  },
  {
    id: 'prg-4',
    universityId: 'inst-kth',
    title: 'M.Sc. Energy Innovation',
    level: "Master's",
    field: 'Energy systems',
    durationMonths: 24,
    tuitionPerYear: 15500,
    currency: 'EUR',
    deliveryMode: 'On campus',
    language: 'English',
    deadline: '2027-01-13',
    deadlineLabel: 'Closes 13 Jan 2027',
    requirements: [
      "Bachelor's in engineering",
      'English proficiency equivalent to Swedish upper secondary English 6',
      'Statement of purpose',
    ],
    scholarshipIds: ['sch-4'],
    careerOutcomes: ['Energy consultant', 'Innovation analyst'],
    provenance: 'user',
  },
  {
    id: 'prg-5',
    universityId: 'inst-abu',
    title: 'M.Sc. Mechanical Engineering',
    level: "Master's",
    field: 'Engineering',
    durationMonths: 24,
    tuitionPerYear: 340000,
    currency: 'NGN',
    deliveryMode: 'On campus',
    language: 'English',
    deadline: '2026-10-15',
    deadlineLabel: 'Closes 15 Oct 2026',
    requirements: [
      'Second class lower or better in Mechanical Engineering',
      'Transcript from awarding institution',
    ],
    scholarshipIds: ['sch-1'],
    careerOutcomes: ['Maintenance engineer', 'Design engineer'],
    provenance: 'verified',
  },
  {
    id: 'prg-6',
    universityId: 'inst-cu',
    title: 'B.Sc. Computer Science',
    level: "Bachelor's",
    field: 'Computing',
    durationMonths: 48,
    tuitionPerYear: 1150000,
    currency: 'NGN',
    deliveryMode: 'On campus',
    language: 'English',
    deadline: '2026-09-12',
    deadlineLabel: 'Closes 12 Sep 2026',
    requirements: [
      'Five O-level credits including Mathematics and English',
      'UTME score of 220 or above',
      'Character reference',
    ],
    scholarshipIds: ['sch-5'],
    careerOutcomes: ['Software engineer', 'Data engineer'],
    provenance: 'verified',
  },
]

export const scholarships: Scholarship[] = [
  {
    id: 'sch-1',
    name: 'NNPC/TotalEnergies National Merit Award',
    funder: 'NNPC Foundation',
    coverage: 'Tuition and annual stipend',
    amountLabel: '₦500,000 / year',
    eligibility: ['Nigerian citizen', 'CGPA 3.5 or above', 'Engineering or science discipline'],
    deadlineLabel: 'Closes 30 Sep 2026',
    level: "Bachelor's and Master's",
    sourceUrlLabel: 'Published by funder',
    provenance: 'verified',
  },
  {
    id: 'sch-2',
    name: 'Justus & Louise van Effen Excellence Scholarship',
    funder: 'TU Delft',
    coverage: 'Full tuition plus living contribution',
    amountLabel: '€30,000 total',
    eligibility: ["Top 5% of bachelor's cohort", 'Non-EU applicant', 'Admitted to an eligible MSc'],
    deadlineLabel: 'Closes 1 Dec 2026',
    level: "Master's",
    sourceUrlLabel: 'Published by institution representative',
    provenance: 'verified',
  },
  {
    id: 'sch-3',
    name: 'UNILAG Faculty of Engineering Bursary',
    funder: 'University of Lagos',
    coverage: 'Partial tuition',
    amountLabel: '₦180,000 / year',
    eligibility: ['Enrolled in the Faculty of Engineering', 'Demonstrated financial need'],
    deadlineLabel: 'Closes 14 Sep 2026',
    level: "Bachelor's and Master's",
    sourceUrlLabel: 'Published by institution representative',
    provenance: 'verified',
  },
  {
    id: 'sch-4',
    name: 'Nordic Energy Futures Grant',
    funder: 'Nordic Energy Research',
    coverage: 'Tuition contribution',
    amountLabel: 'Up to €12,000',
    eligibility: ['Energy-related MSc', 'Research proposal required'],
    deadlineLabel: 'Closes 20 Nov 2026',
    level: "Master's",
    sourceUrlLabel: 'Entered by a student — not yet verified',
    provenance: 'user',
  },
  {
    id: 'sch-5',
    name: 'Covenant University Vice-Chancellor Award',
    funder: 'Covenant University',
    coverage: 'Tuition discount',
    amountLabel: '50% of tuition',
    eligibility: ['UTME score above 300', 'Interview'],
    deadlineLabel: 'Closes 5 Sep 2026',
    level: "Bachelor's",
    sourceUrlLabel: 'Published by institution representative',
    provenance: 'verified',
  },
]

export const myApplications: StudentApplication[] = [
  {
    id: 'sapp-1',
    universityId: 'inst-tud',
    programmeId: 'prg-3',
    stage: 'In progress',
    deadlineLabel: 'Closes 15 Jan 2027',
    feeLabel: '€100 application fee',
    feeRecorded: false,
    requirements: [
      { label: "Bachelor's in engineering or physical sciences", met: true, note: 'In progress — expected Jul 2027' },
      { label: 'IELTS 6.5 overall, no band below 6.0', met: false, note: 'Test booked for 3 Oct' },
      { label: 'Motivation letter and CV', met: false },
      { label: 'Two academic references', met: true, note: 'Dr. Adeyemi and Prof. Okonkwo confirmed' },
    ],
    documents: [
      { label: 'Transcript (partial)', status: 'uploaded', updated: '9 Aug 2026' },
      { label: 'IELTS result', status: 'missing' },
      { label: 'Motivation letter', status: 'missing' },
      { label: 'Passport data page', status: 'verified', updated: '2 Aug 2026' },
    ],
    lastCorrespondence: 'International Office acknowledged your enquiry — 7 Aug 2026',
    contact: 'International Office, TU Delft',
  },
  {
    id: 'sapp-2',
    universityId: 'inst-unilag',
    programmeId: 'prg-2',
    stage: 'Planning',
    deadlineLabel: 'Closes 31 Aug 2026',
    feeLabel: '₦20,000 application fee',
    feeRecorded: false,
    requirements: [
      { label: 'Second class lower or better in an engineering discipline', met: false, note: 'Graduating 2027' },
      { label: 'NYSC discharge certificate', met: false },
      { label: 'Two academic references', met: true },
    ],
    documents: [
      { label: 'Statement of result', status: 'missing' },
      { label: 'Reference letters', status: 'uploaded', updated: '5 Aug 2026' },
    ],
    lastCorrespondence: 'No correspondence yet',
    contact: 'School of Postgraduate Studies',
  },
  {
    id: 'sapp-3',
    universityId: 'inst-kth',
    programmeId: 'prg-4',
    stage: 'Submitted',
    deadlineLabel: 'Closed 13 Jan 2027',
    feeLabel: 'SEK 900 application fee',
    feeRecorded: true,
    requirements: [
      { label: "Bachelor's in engineering", met: true },
      { label: 'English proficiency', met: true, note: 'Waived — English-medium degree' },
      { label: 'Statement of purpose', met: true },
    ],
    documents: [
      { label: 'Transcript', status: 'verified', updated: '28 Jul 2026' },
      { label: 'Statement of purpose', status: 'uploaded', updated: '30 Jul 2026' },
    ],
    lastCorrespondence: 'Application received — 1 Aug 2026',
    contact: 'University Admissions Sweden',
  },
]

export const admissionsFeed: AdmissionsUpdate[] = [
  {
    id: 'adm-1',
    title: 'UNILAG postgraduate applications close in 18 days',
    detail: 'M.Sc. Engineering Management closes 31 August 2026. Statement of result still outstanding on your application.',
    source: 'University of Lagos — Admissions Office',
    time: '2 hours ago',
    kind: 'Deadline',
    provenance: 'verified',
  },
  {
    id: 'adm-2',
    title: 'Van Effen Excellence Scholarship now open',
    detail: 'Full tuition plus living contribution for non-EU applicants admitted to an eligible MSc at TU Delft.',
    source: 'TU Delft — International Office',
    time: 'Yesterday',
    kind: 'Scholarship',
    provenance: 'verified',
  },
  {
    id: 'adm-3',
    title: 'Virtual open day — Sustainable Energy Technology',
    detail: 'Programme leads answering questions on entry requirements and research tracks, 26 August, 14:00 CET.',
    source: 'TU Delft — International Office',
    time: '2 days ago',
    kind: 'Open day',
    provenance: 'verified',
  },
  {
    id: 'adm-4',
    title: 'Nordic Energy Futures Grant deadline added by a student',
    detail: 'Reported as closing 20 November 2026. Not yet confirmed with the funder — verify before relying on it.',
    source: 'Student contribution',
    time: '4 days ago',
    kind: 'Funding',
    provenance: 'user',
  },
]

export function programmeById(id: string) {
  return programmes.find((programme) => programme.id === id)
}

export function universityProfileById(id: string) {
  return universityProfiles.find((university) => university.id === id)
}
